import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Tag, Progress, Tooltip, Avatar, Select, message } from 'antd';
import {
    SearchOutlined,
    ApartmentOutlined,
    BranchesOutlined,
    MenuOutlined,
    PlusOutlined,
    DownOutlined,
    RightOutlined,
    CheckOutlined,
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    HolderOutlined,
    SortAscendingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatName } from '@/utils/nameFormatter';

const PRIORITY_STYLES = {
    Critical: 'bg-[#ffdad6] text-[#93000a] dark:bg-red-950/80 dark:text-red-300',
    High: 'bg-red-50 text-red-600 dark:bg-red-950/60 dark:text-red-400',
    Medium: 'bg-[#c9e6ff] text-[#004c6e] dark:bg-blue-950/80 dark:text-blue-300',
    Low: 'bg-[#e7e8e9] text-[#424750] dark:bg-slate-800 dark:text-slate-300',
};

const STATUS_DOT = {
    Backlog: 'bg-slate-300',
    'In Progress': 'bg-[#28b5fd]',
    'Under Review': 'bg-amber-300',
    Completed: 'bg-emerald-400',
    Closed: 'bg-emerald-400',
    'On Hold': 'bg-orange-300',
    Cancelled: 'bg-red-300',
};

const PRIORITY_WEIGHT = {
    Critical: 4,
    High: 3,
    Medium: 2,
    Low: 1,
};

const getAssigneeSortName = (task) => {
    const assigneeObj = task.assignedTo || task.assignee;
    if (typeof assigneeObj === 'object' && assigneeObj !== null) {
        return `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim();
    }
    return typeof assigneeObj === 'string' ? assigneeObj : '';
};

const getTaskDueDateTimestamp = (task) => {
    const d = task.dueDate || task.timeline?.dueDate || task.endDate;
    return d ? new Date(d).getTime() : 0;
};

const sortTaskList = (list, sortBy) => {
    if (!Array.isArray(list) || list.length <= 1 || sortBy === 'default') return list;
    const sorted = [...list];
    sorted.sort((a, b) => {
        if (sortBy === 'due_asc') {
            const ta = getTaskDueDateTimestamp(a);
            const tb = getTaskDueDateTimestamp(b);
            if (!ta && !tb) return 0;
            if (!ta) return 1;
            if (!tb) return -1;
            return ta - tb;
        }
        if (sortBy === 'due_desc') {
            const ta = getTaskDueDateTimestamp(a);
            const tb = getTaskDueDateTimestamp(b);
            if (!ta && !tb) return 0;
            if (!ta) return 1;
            if (!tb) return -1;
            return tb - ta;
        }
        if (sortBy === 'assignee_asc') {
            return getAssigneeSortName(a).localeCompare(getAssigneeSortName(b));
        }
        if (sortBy === 'assignee_desc') {
            return getAssigneeSortName(b).localeCompare(getAssigneeSortName(a));
        }
        if (sortBy === 'priority_desc') {
            const pa = PRIORITY_WEIGHT[a.priority] || 0;
            const pb = PRIORITY_WEIGHT[b.priority] || 0;
            return pb - pa;
        }
        if (sortBy === 'priority_asc') {
            const pa = PRIORITY_WEIGHT[a.priority] || 0;
            const pb = PRIORITY_WEIGHT[b.priority] || 0;
            return pa - pb;
        }
        if (sortBy === 'name_asc') {
            return (a.name || a.title || '').localeCompare(b.name || b.title || '');
        }
        if (sortBy === 'name_desc') {
            return (b.name || b.title || '').localeCompare(a.name || a.title || '');
        }
        return 0;
    });
    return sorted;
};

export default function ProjectListTab({
    project = {},
    tasks = [],
    onTaskClick,
    onAddMilestone,
    onCompleteMilestone,
    onRefreshTasks,
}) {
    const [taskSearch, setTaskSearch] = useState('');
    const [taskSortBy, setTaskSortBy] = useState('default');
    const [taskViewMode, setTaskViewMode] = useState('hierarchy'); // 'hierarchy' | 'tree' | 'flat'
    const [collapsedMilestones, setCollapsedMilestones] = useState({});
    const [expandedParentTaskIds, setExpandedParentTaskIds] = useState(new Set());
    const [localTasks, setLocalTasks] = useState(tasks);

    // Drag-and-drop state (reordering within list)
    const [draggedTaskId, setDraggedTaskId] = useState(null);
    const [dragOverTaskId, setDragOverTaskId] = useState(null);
    const [dragOverPosition, setDragOverPosition] = useState(null); // 'before' | 'after'
    const dragJustEndedRef = useRef(false);

    // Keep localTasks in sync with prop updates
    useEffect(() => {
        setLocalTasks(tasks);
    }, [tasks]);

    // Filter tasks
    const taskList = useMemo(() => (Array.isArray(localTasks) ? localTasks : []), [localTasks]);
    const regularTasks = useMemo(() => taskList.filter((t) => t.itemType !== 'milestone'), [taskList]);

    // Unify milestones from both tasks collection and embedded project.milestones
    const milestones = useMemo(() => {
        const fromTasks = taskList.filter((t) => t.itemType === 'milestone');
        const fromProject = Array.isArray(project.milestones) ? project.milestones : [];

        const map = new Map();
        fromProject.forEach((pm) => {
            if (!pm) return;
            const key = String(pm._id || pm.id || pm.name || pm.title);
            map.set(key, {
                _id: pm._id || pm.id,
                title: pm.title || pm.name,
                name: pm.name || pm.title,
                status: pm.status || 'Backlog',
                dueDate: pm.dueDate,
                startDate: pm.startDate,
                deliverables: pm.deliverables || [],
                itemType: 'milestone',
                ...pm,
            });
        });

        fromTasks.forEach((tm) => {
            const idKey = String(tm._id);
            const tmTitle = (tm.title || tm.name || '').toLowerCase();
            const existingKey = Array.from(map.keys()).find(
                (k) => k === idKey || (map.get(k)?.title || map.get(k)?.name || '').toLowerCase() === tmTitle
            );

            if (existingKey) {
                map.set(existingKey, { ...map.get(existingKey), ...tm });
            } else {
                map.set(idKey, tm);
            }
        });

        return Array.from(map.values());
    }, [taskList, project.milestones]);

    const isSubtask = (t) => Boolean(t.parentId || t.parentTask);
    const allParentTasks = useMemo(() => regularTasks.filter((t) => !isSubtask(t)), [regularTasks]);
    const allSubtasks = useMemo(() => regularTasks.filter(isSubtask), [regularTasks]);
    const completedTasks = useMemo(() => regularTasks.filter((t) => ['Completed', 'Closed'].includes(t.status)).length, [regularTasks]);

    // Search filter
    const filteredTasks = useMemo(() => {
        if (!taskSearch.trim()) return regularTasks;
        const q = taskSearch.toLowerCase();
        return regularTasks.filter((t) =>
            t.title?.toLowerCase().includes(q) ||
            t.taskId?.toLowerCase().includes(q) ||
            t.taskCode?.toLowerCase().includes(q) ||
            t.itemCode?.toLowerCase().includes(q) ||
            t.assignee?.name?.toLowerCase().includes(q)
        );
    }, [regularTasks, taskSearch]);

    // Group tasks by milestone and apply current sorting
    // We separate top-level parent tasks from child subtasks
    const milestoneMap = useMemo(() => {
        const map = new Map();
        milestones.forEach((m) => {
            const key = String(m._id || m.id || m.title || m.name);
            map.set(key, { milestone: m, tasks: [], parentTasks: [], subtasksByParent: new Map(), orphanSubtasks: [] });
        });
        map.set('no_milestone', {
            milestone: { _id: 'no_milestone', title: 'Tasks Without Milestone', status: 'In Progress' },
            tasks: [],
            parentTasks: [],
            subtasksByParent: new Map(),
            orphanSubtasks: [],
        });

        filteredTasks.forEach((t) => {
            const taskMsId = String(t.milestoneId?._id || t.milestoneId || t.milestone?._id || t.milestone || '');
            const taskMsTitle = (t.milestoneId?.title || t.milestoneId?.name || t.milestone?.title || t.milestone?.name || '').toLowerCase();

            let matchedKey = null;
            if (taskMsId && map.has(taskMsId)) {
                matchedKey = taskMsId;
            } else if (taskMsTitle) {
                for (const [k, v] of map.entries()) {
                    if (k !== 'no_milestone' && (v.milestone.title || v.milestone.name || '').toLowerCase() === taskMsTitle) {
                        matchedKey = k;
                        break;
                    }
                }
            }

            if (matchedKey && map.has(matchedKey)) {
                map.get(matchedKey).tasks.push(t);
            } else {
                map.get('no_milestone').tasks.push(t);
            }
        });

        // Organize into parent tasks and subtasks for hierarchy view
        for (const [k, v] of map.entries()) {
            v.tasks = sortTaskList(v.tasks, taskSortBy);

            // Separate parents and subtasks
            const groupParents = [];
            const groupSubtasks = [];
            v.tasks.forEach((t) => {
                if (isSubtask(t)) {
                    groupSubtasks.push(t);
                } else {
                    groupParents.push(t);
                }
            });

            // Index subtasks by parent ID
            const subMap = new Map();
            const orphanSubtasks = [];
            groupSubtasks.forEach((st) => {
                const pId = String(typeof st.parentId === 'object' ? (st.parentId?._id || st.parentId?.id) : (st.parentId || st.parentTask || ''));
                if (pId) {
                    if (!subMap.has(pId)) subMap.set(pId, []);
                    subMap.get(pId).push(st);
                } else {
                    orphanSubtasks.push(st);
                }
            });

            v.parentTasks = groupParents;
            v.subtasksByParent = subMap;
            v.orphanSubtasks = orphanSubtasks;
        }

        return map;
    }, [milestones, filteredTasks, taskSortBy]);

    // Flat sorted list for flat view
    const sortedFlatTasks = useMemo(() => {
        return sortTaskList(filteredTasks, taskSortBy);
    }, [filteredTasks, taskSortBy]);

    // Tree view grouping
    const treeData = useMemo(() => {
        const parents = sortTaskList(filteredTasks.filter((t) => !isSubtask(t)), taskSortBy);
        const subtasksByParent = new Map();
        filteredTasks.filter(isSubtask).forEach((st) => {
            const pId = String(typeof st.parentId === 'object' ? (st.parentId?._id || st.parentId?.id) : (st.parentId || st.parentTask || ''));
            if (!subtasksByParent.has(pId)) subtasksByParent.set(pId, []);
            subtasksByParent.get(pId).push(st);
        });
        return { parents, subtasksByParent };
    }, [filteredTasks, taskSortBy]);

    const toggleMilestone = (mId) => {
        setCollapsedMilestones((prev) => ({
            ...prev,
            [mId]: !prev[mId],
        }));
    };

    const toggleParentTaskExpand = (taskId) => {
        setExpandedParentTaskIds((prev) => {
            const next = new Set(prev);
            const key = String(taskId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const expandAll = () => {
        setCollapsedMilestones({});
        setExpandedParentTaskIds(new Set(allParentTasks.map((p) => String(p._id))));
    };

    const collapseAll = () => {
        const next = {};
        milestones.forEach((m) => { next[String(m._id)] = true; });
        next['no_milestone'] = true;
        setCollapsedMilestones(next);
        setExpandedParentTaskIds(new Set());
    };

    // Header Sort toggle
    const handleHeaderSort = (field) => {
        if (field === 'name') {
            setTaskSortBy((prev) => (prev === 'name_asc' ? 'name_desc' : 'name_asc'));
        } else if (field === 'assignee') {
            setTaskSortBy((prev) => (prev === 'assignee_asc' ? 'assignee_desc' : 'assignee_asc'));
        } else if (field === 'due') {
            setTaskSortBy((prev) => (prev === 'due_asc' ? 'due_desc' : 'due_asc'));
        } else if (field === 'priority') {
            setTaskSortBy((prev) => (prev === 'priority_desc' ? 'priority_asc' : 'priority_desc'));
        }
    };

    // ── Drag and Drop handlers (Reorder within sections) ─────────────────────
    const handleDragStart = (e, task) => {
        dragJustEndedRef.current = false;
        e.dataTransfer.setData('text/plain', String(task._id));
        e.dataTransfer.effectAllowed = 'move';
        setDraggedTaskId(String(task._id));
    };

    const handleDragEnd = () => {
        setDraggedTaskId(null);
        setDragOverTaskId(null);
        setDragOverPosition(null);
        dragJustEndedRef.current = true;
        setTimeout(() => {
            dragJustEndedRef.current = false;
        }, 150);
    };

    const handleTaskDragOver = (e, targetTask) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';

        const targetId = String(targetTask._id);
        if (draggedTaskId && draggedTaskId === targetId) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const offset = e.clientY - rect.top;
        const position = offset < rect.height / 2 ? 'before' : 'after';

        setDragOverTaskId(targetId);
        setDragOverPosition(position);
    };

    const handleTaskDragLeave = (e, targetTask) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            if (dragOverTaskId === String(targetTask._id)) {
                setDragOverTaskId(null);
                setDragOverPosition(null);
            }
        }
    };

    const handleTaskDrop = (e, targetTask) => {
        e.preventDefault();
        e.stopPropagation();

        const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
        const currentPosition = dragOverPosition;

        // Clean up visual drag markers immediately
        setDraggedTaskId(null);
        setDragOverTaskId(null);
        setDragOverPosition(null);
        dragJustEndedRef.current = true;
        setTimeout(() => {
            dragJustEndedRef.current = false;
        }, 150);

        if (!taskId || String(targetTask._id) === String(taskId)) {
            return;
        }

        // Reorder locally inside the list
        const updatedTasks = [...localTasks];
        const sourceIndex = updatedTasks.findIndex((t) => String(t._id) === String(taskId));
        if (sourceIndex === -1) return;

        const [removed] = updatedTasks.splice(sourceIndex, 1);
        const targetIndex = updatedTasks.findIndex((t) => String(t._id) === String(targetTask._id));
        if (targetIndex !== -1) {
            const insertIndex = currentPosition === 'after' ? targetIndex + 1 : targetIndex;
            updatedTasks.splice(insertIndex, 0, removed);
        } else {
            updatedTasks.push(removed);
        }

        if (taskSortBy !== 'default') {
            setTaskSortBy('default');
        }

        setLocalTasks(updatedTasks);
        message.success('Task reordered');
    };

    return (
        <div className="space-y-4">
            {/* ── KPI Metrics Summary Bar ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                    <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider">Total Tasks</p>
                    <div className="flex items-baseline gap-1.5 mt-1">
                        <p className="text-xl sm:text-2xl font-bold text-[#003263] dark:text-[#a7c8ff]">{regularTasks.length}</p>
                        <span className="text-xs sm:text-sm font-semibold text-slate-400">
                            ({allParentTasks.length} parent · {allSubtasks.length} subtasks)
                        </span>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                    <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider">Top-Level Parents</p>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{allParentTasks.length}</p>
                </div>

                <div className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                    <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider">Nested Subtasks</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{allSubtasks.length}</p>
                </div>

                <div className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                    <p className="text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider">Completed</p>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{completedTasks}</p>
                </div>
            </div>

            {/* ── Search, View Modes & Action Bar ── */}
            <div className="bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 shadow-xs">
                {/* Search */}
                <div className="relative flex-1 min-w-[240px]">
                    <SearchOutlined className="absolute left-3.5 top-3 text-slate-400 text-sm" />
                    <input
                        type="text"
                        placeholder="Search tasks by title, code (TSK-...), or assignee..."
                        value={taskSearch}
                        onChange={(e) => setTaskSearch(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-9 pr-3 py-2 rounded-xl text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                </div>

                <div className="flex items-center gap-2.5 flex-wrap shrink-0">
                    {/* Asana-style Sort By Dropdown */}
                    <div className="flex items-center gap-1.5">
                        <SortAscendingOutlined className="text-slate-400 text-sm" />
                        <Select
                            value={taskSortBy}
                            onChange={(val) => setTaskSortBy(val)}
                            className="min-w-[170px] text-xs sm:text-sm font-semibold"
                            aria-label="Sort tasks"
                        >
                            <Select.Option value="default">Sort: Default (Manual)</Select.Option>
                            <Select.Option value="due_asc">Sort: Due Date (Earliest)</Select.Option>
                            <Select.Option value="due_desc">Sort: Due Date (Latest)</Select.Option>
                            <Select.Option value="assignee_asc">Sort: Assignee (A-Z)</Select.Option>
                            <Select.Option value="assignee_desc">Sort: Assignee (Z-A)</Select.Option>
                            <Select.Option value="priority_desc">Sort: Priority (High to Low)</Select.Option>
                            <Select.Option value="priority_asc">Sort: Priority (Low to High)</Select.Option>
                            <Select.Option value="name_asc">Sort: Task Name (A-Z)</Select.Option>
                            <Select.Option value="name_desc">Sort: Task Name (Z-A)</Select.Option>
                        </Select>
                    </div>

                    {/* View Mode Switcher */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setTaskViewMode('hierarchy')}
                            className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                                taskViewMode === 'hierarchy'
                                    ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                        >
                            <ApartmentOutlined /> Hierarchy
                        </button>
                        <button
                            type="button"
                            onClick={() => setTaskViewMode('tree')}
                            className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                                taskViewMode === 'tree'
                                    ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                        >
                            <BranchesOutlined /> Task Tree
                        </button>
                        <button
                            type="button"
                            onClick={() => setTaskViewMode('flat')}
                            className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                                taskViewMode === 'flat'
                                    ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                        >
                            <MenuOutlined /> Flat List
                        </button>
                    </div>

                    {/* Expand/Collapse All (only for hierarchy and tree) */}
                    {taskViewMode !== 'flat' && (
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={expandAll}
                                className="px-3 py-1.5 text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors border border-slate-200 dark:border-slate-700"
                            >
                                Expand All
                            </button>
                            <button
                                type="button"
                                onClick={collapseAll}
                                className="px-3 py-1.5 text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors border border-slate-200 dark:border-slate-700"
                            >
                                Collapse All
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── View 1: Milestones & Tasks Hierarchy ── */}
            {taskViewMode === 'hierarchy' && (
                <div className="space-y-4">
                    {Array.from(milestoneMap.entries()).map(([mId, group]) => {
                        if (mId === 'no_milestone' && group.tasks.length === 0) return null;

                        const isCollapsed = collapsedMilestones[mId];
                        const ms = group.milestone;
                        const groupCompleted = group.tasks.filter((t) => ['Completed', 'Closed'].includes(t.status)).length;
                        const groupPct = group.tasks.length > 0 ? Math.round((groupCompleted / group.tasks.length) * 100) : 0;
                        const isMilestoneCompleted = ms.status === 'Completed';

                        return (
                            <div
                                key={mId}
                                className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 transition-all overflow-hidden shadow-xs"
                            >
                                {/* Milestone Header */}
                                <div
                                    onClick={() => toggleMilestone(mId)}
                                    className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-colors ${
                                        isMilestoneCompleted
                                            ? 'bg-emerald-50/60 dark:bg-emerald-950/20'
                                            : 'bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-100/60'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-xs text-slate-400">
                                            {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                                        </span>
                                        <span className="font-bold text-sm sm:text-base text-[#003263] dark:text-[#a7c8ff] truncate">
                                            {ms.title}
                                        </span>
                                        {ms.dueDate && (
                                            <span className="text-xs sm:text-sm text-slate-400 shrink-0">
                                                Due: {dayjs(ms.dueDate).format('MMM DD, YYYY')}
                                            </span>
                                        )}
                                        <Tag color={isMilestoneCompleted ? 'green' : 'blue'} className="text-xs sm:text-sm font-bold m-0 py-0.5 px-2">
                                            {ms.status || 'In Progress'}
                                        </Tag>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <div className="hidden sm:flex items-center gap-2 w-32">
                                            <Progress percent={groupPct} size="small" showInfo={false} strokeColor="#003263" />
                                            <span className="text-xs sm:text-sm font-bold text-slate-600 dark:text-slate-300">{groupPct}%</span>
                                        </div>
                                        <span className="text-xs sm:text-sm text-slate-400 font-semibold">
                                            {groupCompleted}/{group.tasks.length} tasks
                                        </span>
                                    </div>
                                </div>

                                {/* Table Column Headers */}
                                {!isCollapsed && (
                                    <>
                                        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-900/80 border-t border-b border-slate-200/80 dark:border-slate-800 text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider select-none">
                                            <div
                                                className="col-span-6 flex items-center gap-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff] transition-colors"
                                                onClick={() => handleHeaderSort('name')}
                                                title="Click to sort by task title"
                                            >
                                                <span className="w-5 text-center">#</span>
                                                <span>Task Name</span>
                                                {taskSortBy === 'name_asc' ? ' ▲' : taskSortBy === 'name_desc' ? ' ▼' : ''}
                                            </div>
                                            <div
                                                className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff] transition-colors"
                                                onClick={() => handleHeaderSort('assignee')}
                                                title="Click to sort by assignee"
                                            >
                                                Assignee {taskSortBy === 'assignee_asc' ? ' ▲' : taskSortBy === 'assignee_desc' ? ' ▼' : ''}
                                            </div>
                                            <div
                                                className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff] transition-colors"
                                                onClick={() => handleHeaderSort('due')}
                                                title="Click to sort by due date"
                                            >
                                                Timeline {taskSortBy === 'due_asc' ? ' ▲' : taskSortBy === 'due_desc' ? ' ▼' : ''}
                                            </div>
                                            <div
                                                className="col-span-2 text-right cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff] transition-colors"
                                                onClick={() => handleHeaderSort('priority')}
                                                title="Click to sort by priority"
                                            >
                                                Priority {taskSortBy === 'priority_desc' ? ' ▲' : taskSortBy === 'priority_asc' ? ' ▼' : ''}
                                            </div>
                                        </div>

                                        {/* Tasks Rows - Parent Tasks with Expandable Subtasks */}
                                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {group.parentTasks.length === 0 && group.orphanSubtasks.length === 0 ? (
                                                <div className="p-5 text-center text-xs sm:text-sm text-slate-400 italic">
                                                    Drag and drop tasks here to assign to this milestone.
                                                </div>
                                            ) : (
                                                <>
                                                    {group.parentTasks.map((task, idx) => {
                                                        const pId = String(task._id);
                                                        const subtasks = group.subtasksByParent.get(pId) || [];
                                                        const isExpanded = expandedParentTaskIds.has(pId);
                                                        const isDone = ['Completed', 'Closed'].includes(task.status);
                                                        const isOverdue = task.dueDate && !isDone && dayjs(task.dueDate).isBefore(dayjs(), 'day');
                                                        const assigneeName = task.assignee?.name || task.assignedTo?.name || 'Unassigned';
                                                        const isDraggingThis = draggedTaskId === pId;
                                                        const isDropTargetBefore = dragOverTaskId === pId && dragOverPosition === 'before';
                                                        const isDropTargetAfter = dragOverTaskId === pId && dragOverPosition === 'after';

                                                        return (
                                                            <div key={pId} className="group/parent">
                                                                {/* Parent Task Row */}
                                                                <div
                                                                    draggable
                                                                    onDragStart={(e) => handleDragStart(e, task)}
                                                                    onDragEnd={handleDragEnd}
                                                                    onDragOver={(e) => handleTaskDragOver(e, task)}
                                                                    onDragLeave={(e) => handleTaskDragLeave(e, task)}
                                                                    onDrop={(e) => handleTaskDrop(e, task)}
                                                                    onClick={(e) => {
                                                                        if (dragJustEndedRef.current) {
                                                                            e.stopPropagation();
                                                                            return;
                                                                        }
                                                                        onTaskClick?.(task);
                                                                    }}
                                                                    className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-blue-50/40 dark:hover:bg-slate-800/40 transition-all cursor-pointer group select-none relative ${
                                                                        isDraggingThis ? 'opacity-30 bg-slate-100 dark:bg-slate-800' : ''
                                                                    } ${
                                                                        isDropTargetBefore ? 'border-t-2 border-blue-500 bg-blue-50/20' : ''
                                                                    } ${
                                                                        isDropTargetAfter ? 'border-b-2 border-blue-500 bg-blue-50/20' : ''
                                                                    }`}
                                                                >
                                                                    {/* Column 1: Expand/Collapse Arrow + Handle + Number + Dot + Code + Title + Subtask Badge */}
                                                                    <div className="col-span-6 flex items-center gap-2.5 min-w-0">
                                                                        <span
                                                                            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing p-0.5 shrink-0"
                                                                            title="Drag to reorder or move across milestones"
                                                                        >
                                                                            <HolderOutlined className="text-xs sm:text-sm" />
                                                                        </span>

                                                                        {/* Subtask toggle chevron or index number */}
                                                                        {subtasks.length > 0 ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleParentTaskExpand(pId);
                                                                                }}
                                                                                className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors text-xs shrink-0"
                                                                                title={isExpanded ? 'Collapse subtasks' : `Expand ${subtasks.length} subtasks`}
                                                                            >
                                                                                {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                                            </button>
                                                                        ) : (
                                                                            <span className="text-xs sm:text-sm font-bold text-slate-400 w-5 shrink-0 text-center">
                                                                                {idx + 1}
                                                                            </span>
                                                                        )}

                                                                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || 'bg-slate-300'}`} />
                                                                        <span className="text-xs sm:text-sm font-mono font-bold text-slate-400 shrink-0">
                                                                            {task.taskId || task.itemCode || task.taskCode || 'TSK'}
                                                                        </span>
                                                                        <span className={`text-xs sm:text-sm font-bold truncate ${isDone ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                                                            {task.title}
                                                                        </span>

                                                                        {/* Clickable Subtask Pill (same as View Modal) */}
                                                                        {subtasks.length > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleParentTaskExpand(pId);
                                                                                }}
                                                                                className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:text-indigo-300 px-2 py-0.5 rounded font-bold shrink-0 transition-colors cursor-pointer border border-indigo-200/60 dark:border-indigo-800/60"
                                                                                title={isExpanded ? 'Click to hide subtasks' : 'Click to show subtasks'}
                                                                            >
                                                                                <span>↳ {subtasks.length} {subtasks.length === 1 ? 'subtask' : 'subtasks'}</span>
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                    {/* Column 2: Assignee */}
                                                                    <div className="col-span-2 flex items-center gap-1.5 min-w-0">
                                                                        <Avatar size={22} className="bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-xs text-white font-bold shrink-0">
                                                                            {assigneeName.charAt(0).toUpperCase()}
                                                                        </Avatar>
                                                                        <span className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">
                                                                            {formatName(assigneeName)}
                                                                        </span>
                                                                    </div>

                                                                    {/* Column 3: Timeline / Due Date */}
                                                                    <div className="col-span-2">
                                                                        {task.dueDate ? (
                                                                            <span className={`text-xs sm:text-sm font-semibold ${isOverdue ? 'text-red-500 font-bold' : 'text-slate-600 dark:text-slate-400'}`}>
                                                                                {dayjs(task.dueDate).format('MMM DD, YYYY')}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-xs sm:text-sm text-slate-400 italic">No Due Date</span>
                                                                        )}
                                                                    </div>

                                                                    {/* Column 4: Priority & Status Tag */}
                                                                    <div className="col-span-2 flex items-center justify-end gap-2 shrink-0">
                                                                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium}`}>
                                                                            {task.priority || 'Medium'}
                                                                        </span>
                                                                        <Tag color={isDone ? 'green' : task.status === 'In Progress' ? 'blue' : 'default'} className="text-xs font-bold m-0 py-0.5 px-2">
                                                                            {task.status}
                                                                        </Tag>
                                                                    </div>
                                                                </div>

                                                                {/* Subtasks nested under this parent (Click to see) */}
                                                                {isExpanded && subtasks.length > 0 && (
                                                                    <div className="bg-slate-50/70 dark:bg-slate-900/50 pl-10 border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100/70 dark:divide-slate-800/50">
                                                                        {subtasks.map((st, sIdx) => {
                                                                            const stDone = ['Completed', 'Closed'].includes(st.status);
                                                                            const stOverdue = st.dueDate && !stDone && dayjs(st.dueDate).isBefore(dayjs(), 'day');
                                                                            const stAssignee = st.assignee?.name || st.assignedTo?.name || 'Unassigned';
                                                                            const isDraggingSt = draggedTaskId === String(st._id);

                                                                            return (
                                                                                <div
                                                                                    key={st._id}
                                                                                    draggable
                                                                                    onDragStart={(e) => handleDragStart(e, st)}
                                                                                    onDragEnd={handleDragEnd}
                                                                                    onClick={(e) => {
                                                                                        if (dragJustEndedRef.current) {
                                                                                            e.stopPropagation();
                                                                                            return;
                                                                                        }
                                                                                        onTaskClick?.(st);
                                                                                    }}
                                                                                    className={`grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-white dark:hover:bg-slate-800/80 transition-all cursor-pointer group select-none ${
                                                                                        isDraggingSt ? 'opacity-30 bg-slate-100 dark:bg-slate-800' : ''
                                                                                    }`}
                                                                                >
                                                                                    <div className="col-span-6 flex items-center gap-2 min-w-0">
                                                                                        <span
                                                                                            className="text-slate-300 hover:text-slate-600 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing p-0.5 shrink-0"
                                                                                            title="Drag subtask to reorder or move"
                                                                                        >
                                                                                            <HolderOutlined className="text-xs" />
                                                                                        </span>
                                                                                        <span className="text-slate-400 font-mono text-xs shrink-0">└─</span>
                                                                                        <span className="text-xs font-mono font-bold text-slate-400 shrink-0">
                                                                                            {st.taskId || st.itemCode || st.taskCode || 'SUB'}
                                                                                        </span>
                                                                                        <span className={`text-xs sm:text-sm font-semibold truncate ${stDone ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                                            {st.title}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="col-span-2 flex items-center gap-1.5 min-w-0">
                                                                                        <Avatar size={20} className="bg-slate-400 text-[10px] text-white font-bold shrink-0">
                                                                                            {stAssignee.charAt(0).toUpperCase()}
                                                                                        </Avatar>
                                                                                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">
                                                                                            {formatName(stAssignee)}
                                                                                        </span>
                                                                                    </div>

                                                                                    <div className="col-span-2 text-xs text-slate-500">
                                                                                        {st.dueDate ? (
                                                                                            <span className={stOverdue ? 'text-red-500 font-bold' : ''}>
                                                                                                {dayjs(st.dueDate).format('MMM DD')}
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="italic text-slate-400">No Date</span>
                                                                                        )}
                                                                                    </div>

                                                                                    <div className="col-span-2 flex items-center justify-end gap-2 shrink-0">
                                                                                        <Tag color={stDone ? 'green' : 'default'} className="text-xs font-bold m-0 py-0.2 px-1.5">
                                                                                            {st.status}
                                                                                        </Tag>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                    {/* Orphan subtasks (if any child task has no matching parent in this milestone) */}
                                                    {group.orphanSubtasks.map((st) => {
                                                        const stDone = ['Completed', 'Closed'].includes(st.status);
                                                        const stAssignee = st.assignee?.name || st.assignedTo?.name || 'Unassigned';
                                                        return (
                                                            <div
                                                                key={st._id}
                                                                draggable
                                                                onDragStart={(e) => handleDragStart(e, st)}
                                                                onDragEnd={handleDragEnd}
                                                                onClick={(e) => {
                                                                    if (dragJustEndedRef.current) {
                                                                        e.stopPropagation();
                                                                        return;
                                                                    }
                                                                    onTaskClick?.(st);
                                                                }}
                                                                className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer"
                                                            >
                                                                <div className="col-span-6 flex items-center gap-2 min-w-0">
                                                                    <span className="text-slate-300 font-mono text-xs">└─</span>
                                                                    <span className="text-xs font-mono font-bold text-slate-400 shrink-0">
                                                                        {st.taskId || st.itemCode || 'SUB'}
                                                                    </span>
                                                                    <span className={`text-xs sm:text-sm font-semibold truncate ${stDone ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                        {st.title}
                                                                    </span>
                                                                    <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.2 rounded font-bold">
                                                                        Subtask
                                                                    </span>
                                                                </div>
                                                                <div className="col-span-2 text-xs text-slate-600 truncate">{formatName(stAssignee)}</div>
                                                                <div className="col-span-2 text-xs text-slate-500">{st.dueDate ? dayjs(st.dueDate).format('MMM DD') : '—'}</div>
                                                                <div className="col-span-2 flex items-center justify-end">
                                                                    <Tag color={stDone ? 'green' : 'default'} className="text-xs font-bold m-0">{st.status}</Tag>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── View 2: Tree View ── */}
            {taskViewMode === 'tree' && (
                <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                    {/* Column Headers */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200/80 dark:border-slate-800 text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider select-none">
                        <div
                            className="col-span-6 flex items-center gap-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('name')}
                        >
                            <span>Parent Task / Subtasks</span>
                            {taskSortBy === 'name_asc' ? ' ▲' : taskSortBy === 'name_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('assignee')}
                        >
                            Assignee {taskSortBy === 'assignee_asc' ? ' ▲' : taskSortBy === 'assignee_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('due')}
                        >
                            Timeline {taskSortBy === 'due_asc' ? ' ▲' : taskSortBy === 'due_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 text-right cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('priority')}
                        >
                            Priority {taskSortBy === 'priority_desc' ? ' ▲' : taskSortBy === 'priority_asc' ? ' ▼' : ''}
                        </div>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {treeData.parents.length === 0 ? (
                            <div className="p-6 text-center text-xs sm:text-sm text-slate-400 italic">
                                No parent tasks found matching search.
                            </div>
                        ) : (
                            treeData.parents.map((parent) => {
                                const pId = String(parent._id);
                                const subtasks = treeData.subtasksByParent.get(pId) || [];
                                const isDone = ['Completed', 'Closed'].includes(parent.status);
                                const assigneeName = parent.assignee?.name || parent.assignedTo?.name || 'Unassigned';
                                const isDraggingThis = draggedTaskId === pId;
                                const isDropTargetBefore = dragOverTaskId === pId && dragOverPosition === 'before';
                                const isDropTargetAfter = dragOverTaskId === pId && dragOverPosition === 'after';

                                return (
                                    <div key={pId} className="group/parent">
                                        {/* Parent row */}
                                        <div
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, parent)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handleTaskDragOver(e, parent)}
                                            onDragLeave={(e) => handleTaskDragLeave(e, parent)}
                                            onDrop={(e) => handleTaskDrop(e, parent)}
                                            onClick={(e) => {
                                                if (dragJustEndedRef.current) {
                                                    e.stopPropagation();
                                                    return;
                                                }
                                                onTaskClick?.(parent);
                                            }}
                                            className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-slate-50/70 dark:hover:bg-slate-800/50 transition-all cursor-pointer select-none ${
                                                isDraggingThis ? 'opacity-30 bg-slate-100 dark:bg-slate-800' : ''
                                            } ${
                                                isDropTargetBefore ? 'border-t-2 border-blue-500 bg-blue-50/20' : ''
                                            } ${
                                                isDropTargetAfter ? 'border-b-2 border-blue-500 bg-blue-50/20' : ''
                                            }`}
                                        >
                                            <div className="col-span-6 flex items-center gap-2.5 min-w-0">
                                                <span className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing p-0.5 shrink-0">
                                                    <HolderOutlined className="text-xs sm:text-sm" />
                                                </span>

                                                {/* Subtask toggle chevron */}
                                                {subtasks.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleParentTaskExpand(pId);
                                                        }}
                                                        className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors text-xs shrink-0"
                                                        title={expandedParentTaskIds.has(pId) ? 'Collapse subtasks' : `Expand ${subtasks.length} subtasks`}
                                                    >
                                                        {expandedParentTaskIds.has(pId) ? <DownOutlined /> : <RightOutlined />}
                                                    </button>
                                                ) : (
                                                    <span className="w-5 shrink-0 text-center text-xs text-slate-400">•</span>
                                                )}

                                                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[parent.status] || 'bg-slate-300'}`} />
                                                <span className="text-xs sm:text-sm font-mono font-bold text-slate-400 shrink-0">
                                                    {parent.taskId || parent.itemCode || 'TSK'}
                                                </span>
                                                <span className={`text-xs sm:text-sm font-bold truncate ${isDone ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                                    {parent.title}
                                                </span>
                                                {subtasks.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleParentTaskExpand(pId);
                                                        }}
                                                        className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:text-indigo-300 px-2 py-0.5 rounded font-bold shrink-0 transition-colors cursor-pointer border border-indigo-200/60 dark:border-indigo-800/60"
                                                    >
                                                        ↳ {subtasks.length} {subtasks.length === 1 ? 'subtask' : 'subtasks'}
                                                    </button>
                                                )}
                                            </div>

                                            <div className="col-span-2 flex items-center gap-1.5 min-w-0">
                                                <Avatar size={22} className="bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-xs text-white font-bold shrink-0">
                                                    {assigneeName.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <span className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">
                                                    {formatName(assigneeName)}
                                                </span>
                                            </div>

                                            <div className="col-span-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-semibold">
                                                {parent.dueDate ? dayjs(parent.dueDate).format('MMM DD, YYYY') : '—'}
                                            </div>

                                            <div className="col-span-2 flex items-center justify-end gap-2 shrink-0">
                                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded ${PRIORITY_STYLES[parent.priority] || PRIORITY_STYLES.Medium}`}>
                                                    {parent.priority || 'Medium'}
                                                </span>
                                                <Tag color={isDone ? 'green' : 'blue'} className="text-xs font-bold m-0 py-0.5 px-2">
                                                    {parent.status}
                                                </Tag>
                                            </div>
                                        </div>

                                        {/* Nested subtasks */}
                                        {expandedParentTaskIds.has(pId) && subtasks.length > 0 && (
                                            <div className="bg-slate-50/50 dark:bg-slate-900/40 pl-6 border-t border-slate-100 dark:border-slate-800/60 divide-y divide-slate-100/60 dark:divide-slate-800/40">
                                                {subtasks.map((st) => {
                                                    const stDone = ['Completed', 'Closed'].includes(st.status);
                                                    const stAssignee = st.assignee?.name || st.assignedTo?.name || 'Unassigned';

                                                    return (
                                                        <div
                                                            key={st._id}
                                                            onClick={(e) => {
                                                                if (dragJustEndedRef.current) {
                                                                    e.stopPropagation();
                                                                    return;
                                                                }
                                                                onTaskClick?.(st);
                                                            }}
                                                            className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-indigo-50/30 dark:hover:bg-slate-800/60 transition-colors cursor-pointer"
                                                        >
                                                            <div className="col-span-6 flex items-center gap-2 min-w-0">
                                                                <span className="text-slate-300 font-mono text-xs">└─</span>
                                                                <span className="text-xs font-mono text-slate-400 shrink-0">
                                                                    {st.taskId || st.itemCode || 'SUB'}
                                                                </span>
                                                                <span className={`text-xs sm:text-sm font-semibold truncate ${stDone ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                    {st.title}
                                                                </span>
                                                            </div>

                                                            <div className="col-span-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300 truncate">
                                                                {formatName(stAssignee)}
                                                            </div>

                                                            <div className="col-span-2 text-xs sm:text-sm text-slate-500">
                                                                {st.dueDate ? dayjs(st.dueDate).format('MMM DD') : '—'}
                                                            </div>

                                                            <div className="col-span-2 flex items-center justify-end gap-2">
                                                                <Tag color={stDone ? 'green' : 'default'} className="text-xs font-bold m-0">
                                                                    {st.status}
                                                                </Tag>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* ── View 3: Flat List View ── */}
            {taskViewMode === 'flat' && (
                <div className="bg-white dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                    {/* Column Headers */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200/80 dark:border-slate-800 text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider select-none">
                        <div
                            className="col-span-6 flex items-center gap-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('name')}
                        >
                            <span className="w-5 text-center">#</span>
                            <span>Task Name</span>
                            {taskSortBy === 'name_asc' ? ' ▲' : taskSortBy === 'name_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('assignee')}
                        >
                            Assignee {taskSortBy === 'assignee_asc' ? ' ▲' : taskSortBy === 'assignee_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('due')}
                        >
                            Timeline {taskSortBy === 'due_asc' ? ' ▲' : taskSortBy === 'due_desc' ? ' ▼' : ''}
                        </div>
                        <div
                            className="col-span-2 text-right cursor-pointer hover:text-[#003263] dark:hover:text-[#a7c8ff]"
                            onClick={() => handleHeaderSort('priority')}
                        >
                            Priority {taskSortBy === 'priority_desc' ? ' ▲' : taskSortBy === 'priority_asc' ? ' ▼' : ''}
                        </div>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {sortedFlatTasks.length === 0 ? (
                            <div className="p-6 text-center text-xs sm:text-sm text-slate-400 italic">
                                No tasks found matching current filters.
                            </div>
                        ) : (
                            sortedFlatTasks.map((task, idx) => {
                                const isDone = ['Completed', 'Closed'].includes(task.status);
                                const isOverdue = task.dueDate && !isDone && dayjs(task.dueDate).isBefore(dayjs(), 'day');
                                const assigneeName = task.assignee?.name || task.assignedTo?.name || 'Unassigned';
                                const isDraggingThis = draggedTaskId === String(task._id);
                                const isDropTargetBefore = dragOverTaskId === String(task._id) && dragOverPosition === 'before';
                                const isDropTargetAfter = dragOverTaskId === String(task._id) && dragOverPosition === 'after';

                                return (
                                    <div
                                        key={task._id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, task)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={(e) => handleTaskDragOver(e, task)}
                                        onDragLeave={(e) => handleTaskDragLeave(e, task)}
                                        onDrop={(e) => handleTaskDrop(e, task)}
                                        onClick={(e) => {
                                            if (dragJustEndedRef.current) {
                                                e.stopPropagation();
                                                return;
                                            }
                                            onTaskClick?.(task);
                                        }}
                                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-blue-50/40 dark:hover:bg-slate-800/40 transition-all cursor-pointer group select-none relative ${
                                            isDraggingThis ? 'opacity-30 bg-slate-100 dark:bg-slate-800' : ''
                                        } ${
                                            isDropTargetBefore ? 'border-t-2 border-blue-500 bg-blue-50/20' : ''
                                        } ${
                                            isDropTargetAfter ? 'border-b-2 border-blue-500 bg-blue-50/20' : ''
                                        }`}
                                    >
                                        <div className="col-span-6 flex items-center gap-2.5 min-w-0">
                                            <span className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing p-0.5 shrink-0">
                                                <HolderOutlined className="text-xs sm:text-sm" />
                                            </span>
                                            <span className="text-xs sm:text-sm font-bold text-slate-400 w-5 shrink-0 text-center">
                                                {idx + 1}
                                            </span>
                                            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${STATUS_DOT[task.status] || 'bg-slate-300'}`} />
                                            <span className="text-xs sm:text-sm font-mono font-bold text-slate-400 shrink-0">
                                                {task.taskId || task.itemCode || task.taskCode || 'TSK'}
                                            </span>
                                            <span className={`text-xs sm:text-sm font-bold truncate ${isDone ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>
                                                {task.title}
                                            </span>
                                            {isSubtask(task) && (
                                                <span className="text-xs bg-purple-50 text-purple-600 dark:bg-purple-950/60 dark:text-purple-300 px-2 py-0.5 rounded font-bold shrink-0">
                                                    Subtask
                                                </span>
                                            )}
                                        </div>

                                        <div className="col-span-2 flex items-center gap-1.5 min-w-0">
                                            <Avatar size={22} className="bg-gradient-to-r from-[#03488A] to-[#00AAF1] text-xs text-white font-bold shrink-0">
                                                {assigneeName.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <span className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">
                                                {formatName(assigneeName)}
                                            </span>
                                        </div>

                                        <div className="col-span-2">
                                            {task.dueDate ? (
                                                <span className={`text-xs sm:text-sm font-semibold ${isOverdue ? 'text-red-500 font-bold' : 'text-slate-600 dark:text-slate-400'}`}>
                                                    {dayjs(task.dueDate).format('MMM DD, YYYY')}
                                                </span>
                                            ) : (
                                                <span className="text-xs sm:text-sm text-slate-400 italic">No Due Date</span>
                                            )}
                                        </div>

                                        <div className="col-span-2 flex items-center justify-end gap-2 shrink-0">
                                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium}`}>
                                                {task.priority || 'Medium'}
                                            </span>
                                            <Tag color={isDone ? 'green' : task.status === 'In Progress' ? 'blue' : 'default'} className="text-xs font-bold m-0 py-0.5 px-2">
                                                {task.status}
                                            </Tag>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
